/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { currencies } from "../../assets/utils/data/items";

import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import plusIcon from "../../assets/images/icons/circle-add-project.svg";
import minusIcon from "../../assets/images/icons/circle-minus-project.svg";
import {
  EndpointsEnum,
  NameIdType,
  prioritiesObj,
  PriorityEnum,
  ProjectMember,
  StageEnum,
} from "../../assets/utils/enums/project";
import { useFormValidation } from "../../assets/utils/hooks/useFormsValidation";
import { validateProject } from "../../assets/utils/validation/form/validator";
import {
  API_URL_COMPANY,
  API_URL_CONTACT,
  API_URL_PRIORITY,
  API_URL_PROJECT,
} from "../../constants";
import getData, { FetchError } from "../../api/project-request";
import toast from "react-hot-toast";
import CustomToast from "../../components/ui/custom-toast";
import { ToastTypeEnum } from "../../assets/utils/enums/toast";
import { ValidationFormsModeEnum } from "../../assets/utils/validation/form/types";
import { authFetch } from "../../api/authFetch";
import AppLayout, { MainBody } from "../../components/layout/app/layout";
import Headline from "../../components/ui/headline";
import { ThemeTypeEnum } from "../../assets/utils/validation/types";
import { HeadlineTypeEnum } from "../../assets/utils/enums/headline";
import { Button } from "../../components/ui/button";
import { COLORS } from "../../assets/utils/enums/colors";
import {
  CaptionText,
  LittleSubHeadline,
  TextBody,
} from "../../components/typography";
import ErrorBlock from "../../components/ui/error-text";
import {
  AddIconBtn,
  AddMemberBody,
  AddMemberItem,
  GeneralItemBlock,
} from "./add";
import { DropdownTypeEnum } from "../../assets/utils/enums/table";
import Dropdown from "../../components/ui/dropdown";
import { BodyDiv } from "../../components/components-style";
import Input from "../../components/ui/inputs";
import {
  formatDateToUkrainian,
  formatIOS,
} from "../../assets/utils/date-formatter";
import DeadlinePicker from "../../components/ui/deadline-picker";
import { ErrorVariantEnum } from "../../assets/utils/enums/error-page";
import handleErrorData from "../../api/error-handler";
import ErrorPage from "../../components/layout/app/error-page";

export default function UpdateProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<any>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const [name, setName] = useState<string>("");
  const [client, setClient] = useState<string>("");
  const [responsibleContact, setResponsibleContact] = useState<string>("");
  const [budget, setBudget] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [priority, setPriority] = useState<string>(PriorityEnum.LOW);
  const [stage, setStage] = useState<string>("");
  const [currency, setCurrency] = useState<string>(currencies[1].symbol);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [deadline, setDeadline] = useState<string>("");

  const [priorities, setPriorities] = useState<NameIdType[]>([]);
  const [filterClients, setFilterClients] = useState<
    { id: number; fullName: string; email: string }[]
  >([]);
  const [filterResponsible, setFilterResponsible] = useState<
    { fullName: string; email: string }[]
  >([]);
  const [filterCompanies, setFilterCompanies] = useState<
    { id: number; name: string; responsibleEmail: string }[]
  >([]);
  const [responsibleForMembers, setResponsibleForMembers] = useState<
    { fullName: string; email: string }[]
  >([]);

  const { errors, validate } = useFormValidation(validateProject);
  const [errorStatus, setErrorStatus] = useState<ErrorVariantEnum | null>(null);

  const loadData = async () => {
    try {
      const data = await getData(`${API_URL_PROJECT}/get-details/${id}`);
      if (!data) {
        setErrorStatus(ErrorVariantEnum.FORBIDDEN);
        return false;
      }

      const normalized = {
        name: data.name || "",
        client: data.clientContactEmail || "",
        company: data.companyName || "",
        description: data.description || "",
        budget: data.budget?.toString() || "",
        currency:
          currencies.find((x) => x.code === data.valutaCode)?.symbol ||
          currencies[1].symbol,
        responsibleContact: data.responsibleUserEmail || "",
        priority: data.priorityName || PriorityEnum.LOW,
        deadline: formatDateToUkrainian(data.deadline) || "",
        stage: (data.projectStageName as StageEnum) || StageEnum.IN_WORKS,
        members:
          data.users?.map((m: ProjectMember) => ({
            email: m.email || "",
            roleName: m.roleName || "",
          })) || [],
      };

      setName(normalized.name);
      setClient(normalized.client);
      setCompany(normalized.company);
      setDescription(normalized.description);
      setBudget(normalized.budget);
      setDeadline(normalized.deadline);
      setCurrency(normalized.currency);
      setResponsibleContact(normalized.responsibleContact);
      setPriority(normalized.priority);
      setStage(normalized.stage);
      setMembers(normalized.members);

      setInitialData(normalized);

      return true;
    } catch (e) {
      const error = e as FetchError;

      handleErrorData(error, setErrorStatus);
      return false;
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      const hasAccess = await loadData();

      if (!hasAccess) {
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    };

    init();
  }, [id]);

  useEffect(() => {
    if (!initialData) return;

    const cleanedMembers = members.map(({ email, roleName }) => ({
      email,
      roleName,
    }));

    const currentData = {
      name,
      client,
      company,
      description,
      budget,
      currency,
      responsibleContact,
      priority,
      deadline,
      stage,
      members: cleanedMembers,
    };

    const cleanedInitialMembers = initialData.members.map(
      ({ email, roleName }: { email: string; roleName: string }) => ({
        email,
        roleName,
      })
    );

    const cleanedInitialData = {
      ...initialData,
      members: cleanedInitialMembers,
    };

    const isSame =
      JSON.stringify(currentData) === JSON.stringify(cleanedInitialData);
    setIsChanged(!isSame);
  }, [
    name,
    client,
    company,
    description,
    budget,
    currency,
    deadline,
    responsibleContact,
    priority,
    stage,
    members,
    initialData,
  ]);

  useEffect(() => {
    const getClients = async () => {
      const data = await getData(`${API_URL_CONTACT}/search?query=${client}`);
      setFilterClients(data);
    };
    const getCompanies = async () => {
      const data = await getData(`${API_URL_COMPANY}/search?query=${company}`);
      setFilterCompanies(data);
    };
    const getResponsibleUsers = async () => {
      const data = await getData(
        `${API_URL_PROJECT}/${EndpointsEnum.RESPONSIBLE_USER}?query=${responsibleContact}`
      );
      setFilterResponsible(data);
    };
    const getMembersUsers = async () => {
      const data = await getData(
        `${API_URL_PROJECT}/${EndpointsEnum.RESPONSIBLE_USER}?query=`
      );
      setResponsibleForMembers(data);
    };

    getClients();
    getCompanies();
    getResponsibleUsers();
    getMembersUsers();

    const getPriorities = async () => {
      const data = await getData(`${API_URL_PRIORITY}/get-all`);
      setPriorities(data);
    };
    getPriorities();
  }, [client, company, responsibleContact]);

  useEffect(() => {
    // автозаповнення компанії, якщо компанія введена повністю
    const getCompanyByClient = async (id: number) => {
      const data = await getData(`${API_URL_CONTACT}/${id}/company`);
      if (data) {
        setCompany(data.company.name);
      } else {
        setClient("");
      }
    };

    const exactClient = filterClients.find((x) => x.email === client);
    if (exactClient) {
      getCompanyByClient(exactClient.id);
    }
  }, [client]);

  useEffect(() => {
    // автозаповнення клієнта, якщо компанія введена повністю
    const getClientByCompany = async (id: number) => {
      const data = await getData(`${API_URL_COMPANY}/${id}/main-contact`);
      if (data) {
        setClient(data.contact.email);
      } else {
        setCompany("");
      }
    };

    const exactCompany = filterCompanies.find((x) => x.name === company);
    if (exactCompany) {
      getClientByCompany(exactCompany.id);
    }
  }, [company]);

  const handleAdd = () => {
    setMembers([
      ...members,
      { email: "", roleName: "", filteredResponsible: responsibleForMembers },
    ]);
  };

  const handleRemove = (index: number) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const handleChange = async (
    index: number,
    field: "email" | "roleName",
    value: string
  ) => {
    const updated = [...members];
    updated[index] = { ...updated[index], [field]: value };

    if (field === "email") {
      const data = await getData(
        `${API_URL_PROJECT}/${EndpointsEnum.RESPONSIBLE_USER}?query=${value}`
      );
      updated[index].filteredResponsible = data;
    }

    setMembers(updated);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const { hasErrors } = validate(
        {
          name,
          client,
          company,
          description,
          budget,
          members,
          responsibleContact,
          priority,
          deadline,
          stage,
        },
        ValidationFormsModeEnum.EDIT
      );

      if (hasErrors) return;

      const response = await authFetch(`${API_URL_PROJECT}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id,
          projectName: name,
          description,
          companyId: filterCompanies.find((x) => x.name === company)?.id || 1,
          valutaCode:
            currencies.find((x) => x.symbol === currency)?.code || "UAH",
          budget: Number.parseInt(budget),
          priorityName: priority,
          projectStageName: stage,
          deadline: formatIOS(deadline),
          responsibleUserEmail: responsibleContact,
          clientContactEmail: client,
          users: members,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.WARNING}
            text={"Перевірте коректність введення даних!"}
          />
        ));
        return;
      }

      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.SUCCESS}
          text={"Дані проєкту успішно оновлено!"}
        />
      ));

      setTimeout(() => {
        navigate("/projects/main");
      }, 300);
    } catch (error) {
      console.error("Update error:", error);
      toast((t) => (
        <CustomToast
          t={t}
          text={"Упс, не вдалося зберегти зміни"}
          type={ToastTypeEnum.ERROR}
        />
      ));
    } finally {
      setTimeout(() => setIsLoading(false), 150);
    }
  };

   if (!isLoading && errorStatus) {
      return (
        <ErrorPage
          title="Редагування неможливе."
          variant={errorStatus}
          onAction={() => navigate(-1)}
          additionalBtn="Дo Проєктів"
          onActionAddBtn={() => navigate("/projects/main")}
        />
      );
    }

  return (
    <AppLayout
      className="d-flex flex-column align-items-center"
      blockType="item-form"
      loaderState={isLoading}
    >
      <Headline
        headline={HeadlineTypeEnum.PROJECTS}
        theme={ThemeTypeEnum.LIGHT}
        btn_text="Повернутися до Проєктів"
        btn_link="/projects/main"
      />
      <MainBody className="align-items-stretch">
        <BodyDiv
          data-class="add-block"
          className="d-flex flex-column align-items-center"
        >
          <CaptionText>Змінити основні дані про проєкт</CaptionText>
          <div className="w-100">
            <span className="w-100 d-flex gap-sm-3 flex-sm-row flex-column">
              <Input
                attribute="nameOfProject"
                value={name}
                setValue={setName}
                errorText={errors.name}
                label="Назва *"
                placeholder="Введіть назву проєкту"
              />
              <Input
                attribute="client"
                value={client}
                setValue={setClient}
                errorText={errors.client}
                label="Клієнт"
                contactsArr={filterClients}
                placeholder="Введіть пошту / ім’я"
              />
            </span>
            <Input
              attribute="budget"
              value={budget}
              setValue={setBudget}
              errorText={errors.budget}
              currency={currency}
              setCurrency={setCurrency}
              label="Бюджет"
              placeholder="Введіть значення"
            />
            <DeadlinePicker value={deadline} setValue={setDeadline} />
            {errors.deadline && <ErrorBlock errorText={errors.deadline} />}
            <Input
              attribute="company"
              value={company}
              setValue={setCompany}
              errorText={errors.company}
              companyArr={filterCompanies}
              label="Компанія"
              placeholder="Введіть назву компанії"
            />
            <Input
              attribute="description"
              value={description}
              setValue={setDescription}
              errorText={""}
              label="Опис"
              placeholder="Введіть опис проєкту"
            />
          </div>
        </BodyDiv>
        <BodyDiv
          data-class="add-block-other"
        >
          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Відповідальний контакт</CaptionText>
            <Input
              attribute="contact"
              value={responsibleContact}
              setValue={setResponsibleContact}
              errorText={errors.responsibleContact}
              label=""
              contactsArr={filterResponsible}
              placeholder="Введіть пошту / ім’я"
            />
          </span>
          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Пріоритет</CaptionText>
            <GeneralItemBlock>
              {priorities.map((item: NameIdType) => {
                const isActive: boolean = priority === item.name;
                return (
                  <Button
                    key={item.id}
                    bgcolor={
                      isActive
                        ? prioritiesObj[item.name as PriorityEnum].bgColor
                        : "transparent"
                    }
                    txtcolor={
                      isActive
                        ? prioritiesObj[item.name as PriorityEnum].txColor
                        : COLORS["bg-text-color"]
                    }
                    brcolor={
                      isActive
                        ? prioritiesObj[item.name as PriorityEnum].bgColor
                        : COLORS["bg-text-color"]
                    }
                    width="100%"
                    type="large"
                    brradius="24px"
                    className=""
                    onClick={() => {
                      setPriority(item.name);
                    }}
                  >
                    <TextBody>{item.name as PriorityEnum}</TextBody>
                    {isActive && (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 32 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M28.708 8.70799L14.708 22.708C14.6151 22.8011 14.5048 22.875 14.3833 22.9254C14.2618 22.9758 14.1315 23.0018 14 23.0018C13.8685 23.0018 13.7382 22.9758 13.6167 22.9254C13.4952 22.875 13.3849 22.8011 13.292 22.708L5.29199 14.708L6.70799 13.292L14 20.586L27.294 7.29199L28.708 8.70799Z"
                          fill={
                            isActive
                              ? prioritiesObj[item.name as PriorityEnum].txColor
                              : "transparent"
                          }
                        />
                      </svg>
                    )}
                  </Button>
                );
              })}
            </GeneralItemBlock>
          </span>
          <span className="w-100 d-flex flex-column">
            <CaptionText className="pb-2 text-center">Статус</CaptionText>
            <Dropdown
              checkedValue={stage}
              setChecked={setStage}
              type={DropdownTypeEnum.STAGE}
            />
            {errors.stage && <ErrorBlock errorText={errors.stage} />}
          </span>
        </BodyDiv>
      </MainBody>
      <BodyDiv
        data-class="add-block-members"
        className="d-flex flex-column align-items-start"
      >
        <span className="d-flex align-items-center justify-content-between w-100">
          <CaptionText>Редагувати учасників цього проєкту</CaptionText>
          <AddIconBtn src={plusIcon} alt="add-icon" onClick={handleAdd} />
        </span>
        <AnimatePresence>
          {members.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="w-100"
            >
              <AddMemberItem key={index}>
                <AddMemberBody>
                  <Input
                    attribute="members"
                    value={member.email}
                    setValue={(val) =>
                      handleChange(index, "email", val as string)
                    }
                    errorText=""
                    label=""
                    contactsArr={
                      member.filteredResponsible || responsibleForMembers
                    }
                    placeholder="Введіть пошту / ім’я"
                  />
                  <Dropdown
                    checkedValue={member.roleName}
                    setChecked={(val) =>
                      handleChange(index, "roleName", val as string)
                    }
                    type={DropdownTypeEnum.PROJECT_ROLES}
                  />
                </AddMemberBody>
                <AddIconBtn
                  src={minusIcon}
                  alt="minus-icon"
                  onClick={() => handleRemove(index)}
                />
              </AddMemberItem>
            </motion.div>
          ))}
        </AnimatePresence>
        {errors.members && <ErrorBlock errorText={errors.members} />}
      </BodyDiv>
      <Button
        bgcolor={COLORS.secondary1}
        txtcolor={COLORS.white}
        brcolor={"transparent"}
        width="30%"
        className="center-btn"
        brradius="24px"
        type="large"
        onClick={handleSave}
        noactive={!isChanged}
        disabled={!isChanged}
      >
        <LittleSubHeadline>Зберегти зміни</LittleSubHeadline>
      </Button>
    </AppLayout>
  );
}
